import React, { memo, useEffect, useState, useCallback } from "react";
import { Input } from "components/Inputs";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";

interface Props {
	propertyId: string;
	defaultState?: any;
	onChange?: (precinctId: string) => void;
}

const constraints = {
	name: {
		presence: {
			message: "^ is required"
		}
	}
};

const AsyncSelect = ({ propertyId, onChange, defaultState: defaultProps }: Props) => {
	const [options, setOptions] = useState([] as any[]);
	const [defaultState, setDefaultState] = useState(defaultProps);
	const [datasource] = useState(
		new Datasource({
			mainModelName: "precinct",
			perPage: 1000,
			currentPage: 1,
			sortBy: "name"
		})
	);

	const onSelectOption = useCallback(
		(precinctId: string) => {
			if (!_.isNil(onChange)) onChange(precinctId);
			setDefaultState((prev: any) => ({ ...prev, precinct_id: precinctId }));
		},
		[onChange]
	);

	// Sync defaultState when prop changes (especially when precinct_id changes externally)
	useEffect(() => {
		if (defaultProps) {
			const newPrecinctId = defaultProps.precinct_id;
			setDefaultState((prev: any) => {
				// Always update if precinct_id is explicitly provided and different
				// This ensures programmatic updates from parent component take precedence
				if (newPrecinctId !== undefined && prev?.precinct_id !== newPrecinctId) {
					// Always create a new object to ensure Input component detects the change
					return { ...defaultProps };
				}
				// Update other properties (like errors) even if precinct_id hasn't changed
				return { ...defaultProps };
			});
		}
	}, [defaultProps]);

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			const options = _.map(response.normalizedMainModelResponse, (precinct: any) => {
				return {
					item: precinct,
					value: precinct.id,
					label: precinct.name
				};
			});
			setOptions([{
				item: {
					id: "",
					name: ""
				},
				value: "",
				label: "Select a precinct"
			}, ...options]);
		});

		return () => sub.unsubscribe();
	}, [datasource]);

	// API call
	useEffect(() => {
		if (_.isEmpty(propertyId)) return;
		datasource.get("v2", "precincts", {
			queryStringParameters: {
				property_id: propertyId,
				sort: datasource.sortBy,
				per_page: datasource.perPage,
				page: datasource.currentPage
			}
		});
	}, [datasource, propertyId, datasource.currentPage, datasource.perPage, datasource.sortBy]);

	if (_.isEmpty(propertyId) || options.length === 0) return null;

	return (
		<Input
			onChange={onSelectOption}
			name="precinct_id"
			defaultState={defaultState}
			required={constraints}
			options={options}
			showIf={true}
		/>
	);
};

export default memo(AsyncSelect);
